import React, { useState, useEffect } from 'react'

import { MatchingFlagProvider } from './MatchingFlagContext'
import { MatchingFlagUI } from './MatchingFlagUI'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { launchToast, ToastTypes } from '../../components'
import {
    useGetEventMatchingPauseConfigQuery,
    useUpdateEventMatchingPauseConfigMutation
} from './utils/useMatchingFlagHooks'
import type { EventMatchingPauseConfig } from './matchingFlag_schemas'

export type MatchingFlagState = EventMatchingPauseConfig

export const MatchingFlagContainer = () => {
    const { eventMatchingPauseUser } = useFlags()

    const [pauseMatchingConfig, setPauseMatchingConfig] = useState<MatchingFlagState>({
        pause_event_matcher: false,
        excluded_lots: null
    })

    const [invalidLots, setInvalidLots] = useState<string[]>([])

    const togglePauseMatching = (value: boolean) => {
        setPauseMatchingConfig((prev) => ({ ...prev, pause_event_matcher: value }))
    }

    const clearInvalidLots = () => {
        setInvalidLots([])
    }

    const { data: getConfig, loading, refetch } = useGetEventMatchingPauseConfigQuery()

    const { updatePauseConfig, loading: updateLoading } = useUpdateEventMatchingPauseConfigMutation({
        onCompleted: async (updateData) => {
            launchToast(ToastTypes.Success, 'Event matching pause config updated successfully')

            const invalidLotsFromBackend = updateData || []
            setInvalidLots(invalidLotsFromBackend)

            if (invalidLotsFromBackend.length > 0) {
                launchToast(
                    ToastTypes.Error,
                    `WARNING: Config saved but, here are the excluded lots that were not in the db: ${invalidLotsFromBackend.join(
                        ', '
                    )}`
                )
            }

            const { data: refetchedData } = await refetch()
            if (refetchedData) {
                setPauseMatchingConfig({
                    pause_event_matcher: refetchedData.pause_event_matcher || false,
                    excluded_lots: refetchedData.excluded_lots || null
                })
            }
        }
    })

    useEffect(() => {
        if (getConfig) {
            setPauseMatchingConfig({
                pause_event_matcher: getConfig.pause_event_matcher || false,
                excluded_lots: getConfig.excluded_lots || null
            })
        }
    }, [getConfig])

    const handleSubmit = () => {
        updatePauseConfig(pauseMatchingConfig)
    }

    const hasChanges = React.useMemo(() => {
        if (!getConfig) return false

        const fetchedToggle = getConfig.pause_event_matcher || false
        const fetchedLots = getConfig.excluded_lots || []

        const toggleChanged = pauseMatchingConfig.pause_event_matcher !== fetchedToggle
        const lotsChanged = JSON.stringify(pauseMatchingConfig.excluded_lots || []) !== JSON.stringify(fetchedLots)

        return toggleChanged || lotsChanged
    }, [getConfig, pauseMatchingConfig])

    if (!eventMatchingPauseUser) return null
    return (
        <MatchingFlagProvider
            context={{
                toggle: pauseMatchingConfig.pause_event_matcher ?? false,
                setToggle: togglePauseMatching,
                excludedLots: pauseMatchingConfig.excluded_lots || [],
                setExcludedLots: (lots: string[]) =>
                    setPauseMatchingConfig((prev) => ({ ...prev, excluded_lots: lots })),
                loading: loading || updateLoading,
                saving: updateLoading,
                handleSubmit: handleSubmit,
                hasChanges: hasChanges,
                invalidLots: invalidLots,
                clearInvalidLots: clearInvalidLots
            }}
        >
            <MatchingFlagUI />
        </MatchingFlagProvider>
    )
}
